import { captureException } from "@sentry/browser"
import {
  initialize as launchDarklyInitialize,
  type LDClient,
} from "launchdarkly-js-client-sdk"
import { derived, readable, writable, type Readable } from "svelte/store"

let launchDarklyClient: LDClient

const launchDarklyReady = writable(false)

type InferFlagValue<T> = T extends boolean | MigrationFlag
  ? boolean
  : T extends string
    ? string
    : T extends number
      ? number
      : T extends infer X
        ? X
        : never

/**
 * Flag definition type for LaunchDarkly flags.
 * Used to define flags with their key, default value, and realtime options.
 */
export type FlagDefinition<T> = {
  key: string
  realtime?: boolean
  defaultValue: InferFlagValue<T>
}

type FlagStore<T> = Readable<InferFlagValue<T>>

type FlagStoreMap<T extends Record<string, FlagDefinition<any>>> = {
  [K in keyof T]: FlagStore<T[K]["defaultValue"]>
}

export class TastyLDClient<T extends Record<string, FlagDefinition<any>>> {
  public flags!: FlagStoreMap<T>
  private readonly client: LDClient
  private readonly context: Record<string, any>

  constructor(
    clientKey: string,
    context: Record<string, any>,
    private readonly flagValues: T,
    bootstrap?: Record<string, unknown>
  ) {
    this.context = context
    this.client = launchDarklyInitialize(
      clientKey,
      context,
      bootstrap ? { bootstrap } : undefined
    )
  }

  private createClientFlagStores(): FlagStoreMap<T> {
    const stores = {} as FlagStoreMap<T>

    for (const name in this.flagValues) {
      if (this.flagValues.hasOwnProperty(name)) {
        const typedName = name as keyof T
        stores[typedName] = this.createFlagStore(typedName)
      }
    }

    return stores
  }

  private createFlagStore<K extends keyof T>(name: K): any {
    const flagInit = this.flagValues[name]
    const { key, defaultValue, realtime = false } = flagInit

    const baseStore = this.createReadableStore(key, defaultValue, realtime)

    return isMigrationFlag(defaultValue)
      ? derived(baseStore, ($flag) => $flag === MigrationFlag.ON)
      : baseStore
  }

  private createReadableStore<V>(
    key: string,
    defaultValue: V,
    realtime: boolean
  ) {
    return readable(this.client.variation(key, defaultValue), (set) => {
      if (realtime) {
        this.client.on("change:" + key, set)
      }
    })
  }

  async initialize() {
    await this.client.waitForInitialization(5).catch((e) => {
      const message =
        "LaunchDarkly client initialization failed. Falling back to default flag values"
      console.error(message, e)

      if (
        e instanceof Error &&
        (e.message.startsWith("network error") ||
          e.name === "LaunchDarklyTimeoutError")
      ) {
        return
      }
      captureException(e)
    })

    // Create typed flag stores using the generalized function
    this.flags = this.createClientFlagStores()

    // Establish module-scoped client for backwards compatibility with legacy flagStore approach
    launchDarklyClient = this.client
    launchDarklyReady.set(true)
  }

  getContext() {
    return this.context
  }
}

/**
 * Retrieves the value of a feature flag from LaunchDarkly.
 *
 * @param key - The key of the feature flag.
 * @param fnChangeListener - Optional callback function to be called when the feature flag value changes.
 * @returns A Promise that resolves to the value of the feature flag.
 */
async function getFlagValue<T>(
  key: string,
  defaultValue: T,
  fnChangeListener?: (param: T) => void
): Promise<T> {
  if (!launchDarklyClient) {
    throw new Error(
      "LaunchDarkly client not initialized. Call initialize() first."
    )
  }

  const flagValue = await launchDarklyClient.variation(key, defaultValue)

  if (fnChangeListener) {
    launchDarklyClient.on("change:" + key, fnChangeListener)
  }
  return flagValue
}

/**
 * Options for the FlagStore class.
 *
 * @template T - The type of the flag value.
 * @property {T} defaultValue - The default value for the flag.
 * @property {boolean} realtime - Whether to enable real-time updates for the flag.
 */
type FlagStoreOptions<T> = {
  defaultValue?: T
  realtime?: boolean
}

/**
 * Retrieves the value of a feature flag from the flag store.
 *
 * @template T - The type of the flag value.
 * @param {string} key - The key of the feature flag.
 * @param {FlagStoreOptions<T>} options - The options for retrieving the flag value.
 * @returns {Readable<T>} - The readable store containing the flag value.
 */
export function flagStore<T>(
  key: string,
  options: FlagStoreOptions<T> = {}
): Readable<T> {
  const { defaultValue = false as T, realtime } = options
  const store = writable(defaultValue)

  const unsubscribe = launchDarklyReady.subscribe((ready) => {
    if (ready) {
      getFlagValue(key, defaultValue, realtime ? store.set : undefined)
        .then(store.set)
        .then(() => unsubscribe()) // Wrap in arrow function to defer evaluation
        .catch((error) => {
          console.warn("LaunchDarkly not configured", error)
          unsubscribe() // Also unsubscribe on error
        })
    }
  })

  return store
}

/**
 * A set of flag values based on launchdarkly defaults for multi-step migration
 */
export enum MigrationFlag {
  OFF = "off",
  ON = "complete",
}

/**
 *  A set of flag values for multi-variation flags specifically for whitelist overriding.
 *
 *  Utilizing this enum approach instead of LD rules in order to override features
 *  hidden by FE specific requirements (i.e. only international customer)
 */
export enum WhitelistFlag {
  ENABLE = "enabled",
  DISABLE = "disabled",
  WHITELIST = "whitelisted",
}

/**
 * Type guard to check if a value is a migration flag
 */
function isMigrationFlag(value: any): value is MigrationFlag {
  return value === MigrationFlag.OFF || value === MigrationFlag.ON
}

const MIGRATION_FLAG_STORE_DEFAULT_OPTIONS: FlagStoreOptions<MigrationFlag> = {
  defaultValue: MigrationFlag.OFF,
  realtime: false,
}

/**
 * Create a flag store for a migration flag represented as a boolean.
 *
 * @param key The key of the feature flag.
 * @param {FlagStoreOptions<T>} options The options for retrieving the flag value.
 * @returns {Readable<boolean>} The derived store containing the boolean flag value.
 */
export function migrationFlagStore(
  key: string,
  options: FlagStoreOptions<MigrationFlag> = {}
) {
  return derived(
    flagStore(key, { ...MIGRATION_FLAG_STORE_DEFAULT_OPTIONS, ...options }),
    ($flag) => $flag === MigrationFlag.ON
  )
}
